import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import App from '@/App.tsx';
import '@/index.css';
import '@/lib/firebase';
import { ErrorView } from '@/views/ErrorView.tsx';
import { PromptView } from '@/views/PromptView.tsx';
import { HistoryView } from '@/views/HistoryView.tsx';
import EditorView from '@/views/EditorView.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthProvider.tsx';
import { Toaster } from '@/components/ui/toaster.tsx';
import { TooltipProvider } from '@/components/ui/tooltip.tsx';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PublicRoute } from '@/components/auth/PublicRoute';
import { LandingPage } from '@/views/LandingPage';
import { AuthPage } from '@/views/AuthPage';
import { TermsPage } from '@/views/TermsPage';
import { PrivacyPage } from '@/views/PrivacyPage';
import { ContactPage } from '@/views/ContactPage';
import '@/i18n/i18n';
import { LocaleProvider } from '@/contexts/LocaleContext';

const queryClient = new QueryClient();

const router = createBrowserRouter(
  [
    {
      element: <PublicRoute />,
      children: [
        {
          path: '/',
          element: <LandingPage />,
        },
        {
          path: '/login',
          element: <AuthPage />,
        },
        {
          path: '/terms',
          element: <TermsPage />,
        },
        {
          path: '/privacy',
          element: <PrivacyPage />,
        },
        {
          path: '/contact',
          element: <ContactPage />,
        },
      ],
    },
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <App />,
          errorElement: <ErrorView />,
          children: [
            {
              path: '/home',
              element: <PromptView />,
              errorElement: <ErrorView />,
            },
            {
              path: '/editor/:id',
              element: <EditorView />,
              errorElement: <ErrorView />,
            },
            {
              path: '/history',
              element: <HistoryView />,
              errorElement: <ErrorView />,
            },
            { path: '*', element: <Navigate to="/home" replace /> },
          ],
        },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ],
  { future: { v7_relativeSplatPath: true } },
);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LocaleProvider>
          <TooltipProvider delayDuration={0}>
            <Toaster />
            <RouterProvider
              router={router}
              future={{ v7_startTransition: true }}
            />
          </TooltipProvider>
        </LocaleProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
